import { Injectable, Injector } from "@angular/core";
import { BehaviorSubject, Observable, of, from } from "rxjs";
import {
  catchError,
  timeout,
  map,
  shareReplay,
  switchMap,
} from "rxjs/operators";
import { LocalStorageService } from "@app/shared/services/local-storage/local-storage.service";
import { LoginService } from "@app/modules/auth/login/services/login.service";
import { _AES_EncryptionService } from "@app/core/services/aes-encryption.service";

export type MenuLoadState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; data: any[] }
  | { status: "failed"; error: any };

const MENU_LOAD_TIMEOUT_MS = 5_000;
const MENU_CACHE_KEY = "__ng_ctx";
const MENU_TOKEN_KEY = "__ng_ctx_t";

@Injectable({ providedIn: "root" })
export class MenuService {
  private readonly _state$ = new BehaviorSubject<MenuLoadState>({
    status: "idle",
  });

  public readonly state$ = this._state$.asObservable();

  public readonly menu$: Observable<any[] | null> = this._state$.pipe(
    map((s) => (s.status === "loaded" ? s.data : null)),
  );

  private _inflight$: Observable<MenuLoadState> | null = null;
  private _loginService: LoginService;

  // Resolves after AES is ready AND hydration attempted — guards wait on this before acting.
  private readonly _hydrationReady: Promise<void>;

  constructor(
    private readonly injector: Injector,
    private readonly localStorageService: LocalStorageService,
    private readonly aes: _AES_EncryptionService,
  ) {
    //  Removed Promise.race 2s timeout — that race caused the timeout to win before
    // crypto-js loaded, skipping _hydrateFromStorage() entirely, causing blank screens.
    // AppComponent.ngOnInit() now calls aes.preload() first for maximum head start.
    this._hydrationReady = this.aes
      .preload()
      .then(() => {
        this._hydrateFromStorage();
      })
      .catch((err) => {
        console.warn(
          "[MenuService] AES preload FAILED — proceeding without cache. State stays idle.",
          err,
        );
      });
  }

  private get loginService(): LoginService {
    if (!this._loginService)
      this._loginService = this.injector.get(LoginService);
    return this._loginService;
  }

  // Public read API

  getMenuSnapshot(): any[] | null {
    const s = this._state$.getValue();
    return s.status === "loaded" ? s.data : null;
  }

  isMenuLoaded(): boolean {
    return this._state$.getValue().status === "loaded";
  }

  isMenuFailed(): boolean {
    return this._state$.getValue().status === "failed";
  }

  // Write API (called by LoginComponent.getUserMenuDetail after login)

  setMenu(menuData: any[]): void {
    //  If AES isn't ready, cache write used to be silently skipped.
    // On next page refresh nothing was in localStorage — blank screen.
    // Now we retry the cache write once AES resolves.
    this._writeCacheWhenReady(menuData);
    this._state$.next({ status: "loaded", data: menuData });
  }

  private _writeCacheWhenReady(menuData: any[]): void {
    if (!this._tryWriteCache(menuData)) {
      this.aes
        .preload()
        .then(() => {
          const ok = this._tryWriteCache(menuData);
        })
        .catch(() => {});
    }
  }

  // Returns true on success, false if AES threw (not ready). Does NOT swallow AES errors.
  private _tryWriteCache(menuData: any[]): boolean {
    try {
      const plainText = JSON.stringify(menuData);
      const encrypted = this.aes.encrypt(plainText); // throws if CryptoJS not loaded
      const token =
        this.localStorageService.getLocalStorageItem("flexYear-token");
      // Last 16 chars of token as fingerprint — detects logout+re-login so stale cache is never served
      const tokenFingerprint = token ? token.slice(-16) : "";
      localStorage.setItem(MENU_CACHE_KEY, encrypted);
      localStorage.setItem(MENU_TOKEN_KEY, tokenFingerprint);
      return true;
    } catch (e) {
      if (!(e instanceof Error && e.message.startsWith("[AES]"))) {
      }
      return false;
    }
  }

  // Called on logout — wipes both cache keys and resets in-memory state
  clearMenu(): void {
    localStorage.removeItem(MENU_CACHE_KEY);
    localStorage.removeItem(MENU_TOKEN_KEY);
    this._inflight$ = null;
    this._state$.next({ status: "idle" });
  }

  // Core load logic

  // Entry point for all guards — waits for hydration before acting
  loadMenu(): Observable<MenuLoadState> {
    return from(this._hydrationReady).pipe(
      switchMap(() => {
        return this._doLoadMenu();
      }),
    );
  }

  private _doLoadMenu(): Observable<MenuLoadState> {
    const current = this._state$.getValue();

    // Hydration already populated from localStorage — short circuit
    if (current.status === "loaded") {
      return of(current);
    }
    if (current.status === "failed") {
      return of(current);
    }

    // Parallel guard calls share the single in-flight request
    if (this._inflight$) {
      return this._inflight$;
    }

    const token =
      this.localStorageService.getLocalStorageItem("flexYear-token");
    const userId = this.localStorageService.getLocalStorageItem("user_id");
    const companyId =
      this.localStorageService.getLocalStorageItem("company_id");

    // No credentials — fail fast so RoleGuard redirects to login instead of blank page
    if (!token || token === "null" || !userId || !companyId) {
      const failState: MenuLoadState = {
        status: "failed",
        error: "No credentials — redirect to login",
      };
      this._state$.next(failState);
      return of(failState);
    }
    this._state$.next({ status: "loading" });

    // shareReplay(1) ensures every subscriber gets the terminal emission even after source completes
    this._inflight$ = this.loginService.getMenuDetails(userId, companyId).pipe(
      timeout(MENU_LOAD_TIMEOUT_MS),
      map((res: any) => {
        // Treat null/undefined/empty object as backend failure
        if (
          !res ||
          (typeof res === "object" && Object.keys(res).length === 0)
        ) {
          this._state$.next({
            status: "failed",
            error: "Empty response from user-menu API",
          });
          this._inflight$ = null;
          return this._state$.getValue();
        }

        if (res?.status && res?.data) {
          this.setMenu(res.data);
        } else {
          this._state$.next({
            status: "failed",
            error: "Unexpected menu payload",
          });
        }
        this._inflight$ = null;
        return this._state$.getValue();
      }),
      catchError((err) => {
        if (err?.name === "TimeoutError") {
        } else {
        }
        const failState: MenuLoadState = { status: "failed", error: err };
        this._state$.next(failState);
        this._inflight$ = null;
        return of(failState);
      }),
      shareReplay(1),
    );

    return this._inflight$;
  }

  // Private helpers

  private _hydrateFromStorage(): void {
    const encrypted = localStorage.getItem(MENU_CACHE_KEY);
    if (!encrypted) {
      return;
    }

    try {
      // Fingerprint check — discard cache if token changed (logout/re-login/expiry)
      const storedFingerprint = localStorage.getItem(MENU_TOKEN_KEY) ?? "";
      const currentToken =
        this.localStorageService.getLocalStorageItem("flexYear-token");
      const currentFingerprint = currentToken ? currentToken.slice(-16) : "";

      if (storedFingerprint !== currentFingerprint) {
        localStorage.removeItem(MENU_CACHE_KEY);
        localStorage.removeItem(MENU_TOKEN_KEY);
        return;
      }
      const plainText = this.aes.decrypt(encrypted);
      if (!plainText) {
        localStorage.removeItem(MENU_CACHE_KEY);
        localStorage.removeItem(MENU_TOKEN_KEY);
        return;
      }

      const data = JSON.parse(plainText);
      if (Array.isArray(data) && data.length > 0) {
        this._state$.next({ status: "loaded", data });
      } else {
      }
    } catch (e) {
      localStorage.removeItem(MENU_CACHE_KEY);
      localStorage.removeItem(MENU_TOKEN_KEY);
    }
  }
}
